import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../store/store";
import {
  submitKYC,
  getProfile,
  clearUserError,
  setIsUploadingKYC,
  setUploadProgress,
} from "../../store/slices/userSlice";
import Header from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";

import {
  DocumentTextIcon,
  CameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";

const KycVerification: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    kycStatus,
    isLoading,
    isSubmittingKYC,
    isUploadingKYC,
    uploadProgress,
    error,
    profile,
  } = useSelector((state: RootState) => state.user);
  const { user } = useSelector((state: RootState) => state.auth);

  const [documentType, setDocumentType] = useState("id_card");
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [backImage, setBackImage] = useState<File | null>(null);
  const [selfieImage, setSelfieImage] = useState<File | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [hasLoadedProfile, setHasLoadedProfile] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadStarted, setUploadStarted] = useState(false);

  const frontInputRef = useRef<HTMLInputElement>(null);
  const backInputRef = useRef<HTMLInputElement>(null);
  const selfieInputRef = useRef<HTMLInputElement>(null);

  // Load user profile on component mount
  useEffect(() => {
    if (user) {
      dispatch(getProfile())
        .unwrap()
        .then(() => {
          setHasLoadedProfile(true);
        })
        .catch((err) => {
          console.error("Failed to load profile:", err);
          setHasLoadedProfile(true);
        });
    }
  }, [dispatch, user]);

  // Check if user can submit KYC
  const canSubmitKYC = React.useMemo(() => {
    if (!user || !profile) return false;

    // Check email verification
    if (!user.email_verified && !profile.email_verified) {
      return false;
    }

    // Check current KYC status
    const currentStatus = kycStatus || profile?.kyc_status;

    // Only show form if status is null, empty, or "rejected"
    if (
      !currentStatus ||
      currentStatus === "" ||
      currentStatus === "null" ||
      currentStatus === "rejected"
    ) {
      return true;
    }

    return false;
  }, [user, profile, kycStatus]);

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "front" | "back" | "selfie"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationError(
        `${
          type === "selfie" ? "Selfie" : "Document"
        } image must be less than 5MB`
      );
      e.target.value = ""; // Clear the input
      return;
    }

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      setValidationError("Only JPG and PNG images are allowed");
      e.target.value = ""; // Clear the input
      return;
    }

    setValidationError(null);

    if (type === "front") setFrontImage(file);
    else if (type === "back") setBackImage(file);
    else setSelfieImage(file);
  };

  const handleSubmit = async () => {
    if (!frontImage || !selfieImage) {
      setValidationError("Please upload front document image and selfie");
      return;
    }

    // Reset any previous errors
    setValidationError(null);
    dispatch(clearUserError());
    setUploadStarted(true);

    // Create FormData
    const formData = new FormData();
    formData.append("document_type", documentType);

    // Append files with proper field names
    formData.append("front_image", frontImage, frontImage.name);
    formData.append("selfie", selfieImage, selfieImage.name);

    if (backImage) {
      formData.append("back_image", backImage, backImage.name);
    }

    // Log form data for debugging
    console.log("FormData prepared for submission:");
    const entries = Array.from(formData.entries());
    entries.forEach(([key, value]) => {
      console.log(
        `${key}:`,
        value instanceof File ? `${value.name} (${value.type})` : value
      );
    });

    try {
      console.log("Dispatching KYC submission...");
      dispatch(setIsUploadingKYC(true));
      dispatch(setUploadProgress(30)); // Simulate upload start

      const result = await dispatch(submitKYC(formData)).unwrap();
      console.log("KYC submission result:", result);

      dispatch(setUploadProgress(100));

      if (result?.kyc_status === "pending") {
        // Show success message
        setTimeout(() => {
          alert(
            "✅ KYC documents submitted successfully! Status: Pending review."
          );

          // Refresh profile to get updated KYC status
          dispatch(getProfile());

          // Reset form
          setFrontImage(null);
          setBackImage(null);
          setSelfieImage(null);
          setUploadStarted(false);
          dispatch(setIsUploadingKYC(false));
          dispatch(setUploadProgress(0));
        }, 500);
      }
    } catch (error: any) {
      console.error("KYC submission failed:", error);
      setUploadStarted(false);
      dispatch(setIsUploadingKYC(false));
      dispatch(setUploadProgress(0));

      // Show error to user
      if (error.includes("timeout")) {
        alert(
          "⏰ Request timeout. Please check your internet connection and try again."
        );
      } else if (error.includes("Network error")) {
        alert(
          "🌐 Network error. Please check your internet connection and try again."
        );
      } else if (error.includes("Validation failed")) {
        alert(`❌ ${error}`);
      } else {
        alert(`❌ Failed to submit KYC: ${error}`);
      }
    }
  };

  const handleReturnToDashboard = () => {
    navigate("/dashboard");
  };

  const handleResubmit = () => {
    // Clear form for resubmission
    setFrontImage(null);
    setBackImage(null);
    setSelfieImage(null);
    setDocumentType("id_card");
    setValidationError(null);
    setUploadStarted(false);
  };

  const handleVerifyEmail = () => {
    navigate("/verify-email");
  };

  const renderStatus = () => {
    const currentStatus = kycStatus || profile?.kyc_status;

    if (!currentStatus || currentStatus === "" || currentStatus === "null") {
      // Check if email is verified
      const emailVerified = user?.email_verified || profile?.email_verified;

      if (!emailVerified) {
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-yellow-900 flex items-center justify-center mx-auto mb-4">
              <InformationCircleIcon className="w-8 h-8 text-yellow-300" />
            </div>
            <h3 className={globalStyle.h5}>Email Verification Required</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Please verify your email before submitting KYC documents.
            </p>
            <div className="mt-6">
              <Button onClick={handleVerifyEmail} fullWidth>
                Verify Email
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-blue-900 flex items-center justify-center mx-auto mb-4">
            <InformationCircleIcon className="w-8 h-8 text-blue-300" />
          </div>
          <h3 className={globalStyle.h5}>KYC Verification Required</h3>
          <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
            You need to submit KYC documents to access all features.
          </p>
          <div className="mt-6">
            <Button onClick={() => {}} fullWidth>
              Submit Documents
            </Button>
          </div>
        </div>
      );
    }

    switch (currentStatus.toLowerCase()) {
      case "approved":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-8 h-8 text-green-300" />
            </div>
            <h3 className={globalStyle.h5}>KYC Verified</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Your identity has been successfully verified.
            </p>
            <p className={globalStyle.caption + " text-gray-500"}>
              You can now access all platform features.
            </p>
            <div className="mt-6">
              <Button onClick={handleReturnToDashboard} fullWidth>
                Go to Dashboard
              </Button>
            </div>
          </div>
        );

      case "pending":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-yellow-900 flex items-center justify-center mx-auto mb-4">
              <ClockIcon className="w-8 h-8 text-yellow-300" />
            </div>
            <h3 className={globalStyle.h5}>Verification Pending</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Your KYC documents are under review.
            </p>
            <p className={globalStyle.caption + " text-gray-500"}>
              This process usually takes 24-48 hours.
            </p>
            <div className="mt-6 space-y-3">
              <Button onClick={handleReturnToDashboard} fullWidth>
                Return to Dashboard
              </Button>
              <Button
                variant="tertiary"
                fullWidth
                onClick={() => {
                  dispatch(getProfile());
                }}
              >
                Refresh Status
              </Button>
            </div>
          </div>
        );

      case "rejected":
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
              <XCircleIcon className="w-8 h-8 text-red-300" />
            </div>
            <h3 className={globalStyle.h5}>Verification Rejected</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Your documents were not approved. Please resubmit with valid
              documents.
            </p>
            <p className={globalStyle.caption + " text-gray-500"}>
              Check the instructions for proper document requirements.
            </p>
            <div className="mt-6 space-y-3">
              <Button onClick={handleResubmit} fullWidth>
                Resubmit Documents
              </Button>
              <Button
                variant="tertiary"
                onClick={() => setShowInstructions(true)}
                fullWidth
              >
                View Instructions
              </Button>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-900 flex items-center justify-center mx-auto mb-4">
              <InformationCircleIcon className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className={globalStyle.h5}>KYC Status Unknown</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              Status: {currentStatus || "Not submitted"}
            </p>
            <div className="mt-6 space-y-3">
              <Button onClick={handleResubmit} fullWidth>
                Submit KYC Documents
              </Button>
              <Button
                variant="tertiary"
                fullWidth
                onClick={() => navigate("/dashboard")}
              >
                Return to Dashboard
              </Button>
            </div>
          </div>
        );
    }
  };

  // Show loading while profile is being loaded
  if (!hasLoadedProfile || isLoading) {
    return (
      <div className={globalStyle.container}>
        <Header />
        <main className="page-content">
          <div className="mb-6">
            <h1 className={globalStyle.h3}>KYC Verification</h1>
            <p className={globalStyle.body2}>
              Verify your identity to unlock all platform features
            </p>
          </div>
          <Card padding="lg">
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
              <p className={globalStyle.body2 + " text-gray-400"}>
                Loading KYC status...
              </p>
            </div>
          </Card>
        </main>
        <BottomNav />
      </div>
    );
  }

  // Show KYC form if user can submit
  if (canSubmitKYC) {
    return (
      <div className={globalStyle.container}>
        <Header />

        <main className="page-content">
          {/* Header */}
          <div className="mb-6">
            <h1 className={globalStyle.h3}>KYC Verification</h1>
            <p className={globalStyle.body2 + " text-gray-400"}>
              Verify your identity to unlock all platform features
            </p>
          </div>

          <Card padding="lg">
            {(error || validationError) && (
              <Alert
                type="error"
                message={error || validationError || ""}
                className="mb-6"
                onClose={() => {
                  if (error) dispatch(clearUserError());
                  if (validationError) setValidationError(null);
                }}
              />
            )}

            <div className="mb-6">
              <h3 className={globalStyle.h5}>Document Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                {[
                  { value: "id_card", label: "ID Card" },
                  { value: "passport", label: "Passport" },
                  { value: "driver_license", label: "Driver's License" },
                ].map((doc) => (
                  <button
                    key={doc.value}
                    className={`p-4 border rounded-lg text-center transition-colors ${
                      documentType === doc.value
                        ? "border-primary bg-primary-900 text-white"
                        : "border-gray-700 hover:border-gray-600 text-gray-300"
                    }`}
                    onClick={() => setDocumentType(doc.value)}
                  >
                    {doc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Document Front */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className={globalStyle.h6}>Front of Document *</h4>
                <button
                  type="button"
                  onClick={() => setShowInstructions(true)}
                  className="text-sm text-primary hover:text-primary-300 flex items-center"
                >
                  <InformationCircleIcon className="w-4 h-4 mr-1" />
                  Instructions
                </button>
              </div>

              <input
                type="file"
                ref={frontInputRef}
                onChange={(e) => handleImageUpload(e, "front")}
                accept="image/jpeg,image/png,image/jpg"
                className="hidden"
              />

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
                  frontImage
                    ? "border-green-500 bg-green-900"
                    : "border-gray-700 hover:border-gray-600"
                }`}
                onClick={() => frontInputRef.current?.click()}
              >
                {frontImage ? (
                  <div>
                    <CheckCircleIcon className="w-12 h-12 text-green-300 mx-auto mb-2" />
                    <p className="text-sm text-green-300 truncate">
                      {frontImage.name}
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      {(frontImage.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <CameraIcon className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">
                      Click to upload front image
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG up to 5MB
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Document Back (Optional) */}
            <div className="mb-6">
              <h4 className={`${globalStyle.h6} mb-3`}>
                Back of Document (Optional)
              </h4>
              <p className="text-sm text-gray-500 mb-3">
                {documentType === "passport"
                  ? "Optional for passports"
                  : "Required for ID cards and driver's licenses"}
              </p>

              <input
                type="file"
                ref={backInputRef}
                onChange={(e) => handleImageUpload(e, "back")}
                accept="image/jpeg,image/png,image/jpg"
                className="hidden"
              />

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
                  backImage
                    ? "border-green-500 bg-green-900"
                    : "border-gray-700 hover:border-gray-600"
                }`}
                onClick={() => backInputRef.current?.click()}
              >
                {backImage ? (
                  <div>
                    <CheckCircleIcon className="w-12 h-12 text-green-300 mx-auto mb-2" />
                    <p className="text-sm text-green-300 truncate">
                      {backImage.name}
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      {(backImage.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <DocumentTextIcon className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">
                      Click to upload back image
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {documentType === "passport"
                        ? "Optional - Only if you have back page"
                        : "Recommended - Both sides needed"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Selfie */}
            <div className="mb-8">
              <h4 className={`${globalStyle.h6} mb-3`}>
                Selfie with Document *
              </h4>

              <input
                type="file"
                ref={selfieInputRef}
                onChange={(e) => handleImageUpload(e, "selfie")}
                accept="image/jpeg,image/png,image/jpg"
                className="hidden"
              />

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer ${
                  selfieImage
                    ? "border-green-500 bg-green-900"
                    : "border-gray-700 hover:border-gray-600"
                }`}
                onClick={() => selfieInputRef.current?.click()}
              >
                {selfieImage ? (
                  <div>
                    <CheckCircleIcon className="w-12 h-12 text-green-300 mx-auto mb-2" />
                    <p className="text-sm text-green-300 truncate">
                      {selfieImage.name}
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      {(selfieImage.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-green-400 mt-1">
                      Click to change
                    </p>
                  </div>
                ) : (
                  <div>
                    <CameraIcon className="w-12 h-12 text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">
                      Click to upload selfie
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Hold your document next to your face
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Progress Indicator */}
            {(isUploadingKYC || uploadStarted) && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-300">
                    Uploading...
                  </span>
                  <span className="text-sm text-primary">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2.5">
                  <div
                    className="bg-primary h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                {uploadProgress < 100 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Please don't close this window while uploading...
                  </p>
                )}
              </div>
            )}

            {/* Requirements */}
            <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-yellow-300 mb-2">
                Requirements
              </h4>
              <ul className="text-sm text-yellow-200 space-y-1">
                <li>• All images must be clear and readable</li>
                <li>• Documents must be valid and not expired</li>
                <li>• Selfie must clearly show your face and document</li>
                <li>• Maximum file size: 5MB per image</li>
                <li>• Allowed formats: JPG, PNG</li>
                <li>• All documents must be in color and unedited</li>
              </ul>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              loading={isSubmittingKYC || isUploadingKYC}
              disabled={
                !frontImage || !selfieImage || isSubmittingKYC || isUploadingKYC
              }
              fullWidth
              size="lg"
              leftIcon={
                isUploadingKYC ? (
                  <ArrowUpTrayIcon className="w-5 h-5 animate-bounce" />
                ) : undefined
              }
            >
              {isSubmittingKYC || isUploadingKYC
                ? uploadProgress < 100
                  ? "Uploading..."
                  : "Processing..."
                : "Submit for Verification"}
            </Button>
          </Card>
        </main>

        <BottomNav />

        {/* Instructions Modal */}
        <Modal
          isOpen={showInstructions}
          onClose={() => setShowInstructions(false)}
          title="KYC Instructions"
          size="lg"
        >
          <div className="space-y-6">
            <div className="bg-blue-900 border border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-300 mb-2">
                Important Notes
              </h4>
              <ul className="text-sm text-blue-200 space-y-1">
                <li>• Your information is encrypted and securely stored</li>
                <li>• Verification typically takes 24-48 hours</li>
                <li>• You'll receive email notifications about your status</li>
                <li>• All documents must be in color and unedited</li>
                <li>• Ensure your email is verified before submitting KYC</li>
              </ul>
            </div>

            <div>
              <h4 className={`${globalStyle.h6} mb-3 text-white`}>
                How to Take Proper Photos
              </h4>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-medium text-primary">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">Document Photos</p>
                    <p className="text-sm text-gray-400">
                      Ensure all text is clearly visible, no glare, and corners
                      are not cut off.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-medium text-primary">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      Selfie with Document
                    </p>
                    <p className="text-sm text-gray-400">
                      Hold the document next to your face. Both your face and
                      the document must be clearly visible.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 rounded-full bg-primary-900 flex items-center justify-center mr-3 flex-shrink-0">
                    <span className="text-sm font-medium text-primary">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-white">Lighting</p>
                    <p className="text-sm text-gray-400">
                      Take photos in a well-lit area without shadows on the
                      document or your face.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-red-900 border border-red-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-red-300 mb-2">
                Common Reasons for Rejection
              </h4>
              <ul className="text-sm text-red-200 space-y-1">
                <li>• Blurry or unreadable images</li>
                <li>• Expired documents</li>
                <li>• Mismatched information</li>
                <li>• Edited or filtered photos</li>
                <li>• Document corners cut off</li>
                <li>• Face not visible in selfie</li>
                <li>• Document not visible in selfie</li>
              </ul>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setShowInstructions(false)}>Got It</Button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  // Show KYC status page
  return (
    <div className={globalStyle.container}>
      <Header />
      <main className="page-content">
        <div className="mb-6">
          <h1 className={globalStyle.h3}>KYC Verification</h1>
          <p className={globalStyle.body2 + " text-gray-400"}>
            Verify your identity to unlock all platform features
          </p>
        </div>
        <Card padding="lg">{renderStatus()}</Card>
      </main>
      <BottomNav />
    </div>
  );
};

export default KycVerification;
